import { City } from "../components/searchbox/searchTab/flights/RoutePickerModel";
import { ApiStations, ApiCountry, ApiStation } from "Shared/models/ApiStations";
import { API_STATION_URL, CHILE_COUNTRY_CODE } from "Services/constants";
import { RouteSelectorOpts } from "ComponentModels/RouteSelectorOpts";
import { locale } from "Services/Globals";
import { useState } from "haunted";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { removeTrailingSlash } from "Services/common";
import { createHeaders } from "./HeaderHelper";
import { useStationOrderStrategyFactory } from "./Strategies/useStationOrderStrategyFactory";
import { useReduxState } from "Shared/redux/useReduxState";
import { Country } from "Components/searchbox/searchTab/flights/RoutePickerModel";

interface StationProps {
	opts: RouteSelectorOpts;
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
}

export const useStations = (props: StationProps): [ApiCountry[], (city: ApiStation) => ApiCountry[]] => {
	const [userInfo] = useReduxState("userInfo");
	const [navitaireSettings] = useReduxState("navitaireSettings");

	const factory = useStationOrderStrategyFactory(props.opts.culture);

	const [countryStations, setCountryStations] = useState<ApiCountry[]>([]);
	const [loader, setLoader] = useState<LoaderInstance>();
	const [_countries, setCountries] = useReduxState("countries");

	const isCountryAvailable = (apiCountry: ApiCountry): boolean => {
		if (!navitaireSettings?.stationSettings) throw new Error("No StationSettings");

		return navitaireSettings?.stationSettings.Countries.some(
			(country) =>
				country.Code.toLowerCase() === apiCountry.information.code.toLowerCase() &&
				!country.IsHidden &&
				((!userInfo?.PeruCompra?.IsMember && !userInfo?.PeruCompra?.IsAdmin) ||
					country.IsAvailableForPeruCompras) &&
				(country.Code.toLowerCase() === CHILE_COUNTRY_CODE.toLowerCase() || !userInfo?.ChileCompra?.IsMember)
		);
	};

	const availableDestinationCountriesForStation = (city: ApiStation): ApiCountry[] =>
		countryStations
			.map((country) => ({
				...country,
				regions: country.regions
					.map((region) => ({
						...region,
						stations: region.stations.filter((station) => city.markets.includes(station.information.code)),
					}))
					.filter((region) => region.stations.length > 0),
			}))
			.filter((country) => country.regions.length > 0);

	const loadApiCountries = async (): Promise<ApiCountry[]> => {
		const headers = createHeaders();

		const url = createStationsUrl();
		const response = await fetch(url, { headers });

		if (response.ok) {
			const responseJson = (await response.json()) as ApiStations;

			return responseJson.countries;
		} else {
			throw new Error("Network response was not ok.");
		}
	};

	const createStationsUrl = () => {
		if (window.JetSmart.StationsOptions.Mode === "DirectFareCache") {
			const baseUrl = removeTrailingSlash(window.JetSmart.StationsOptions.BaseAddress);

			return `${baseUrl}/stations?culture=${locale}`;
		}

		return `${API_STATION_URL}?locale=${locale}`;
	};

	const loadStations = async () => {
		setLoader(props.startLoading());
		const countries = await loadApiCountries();
		const strategy = factory.create();
		const orderedCountries = strategy().orderStations(countries);
		await strategy().postStep();
		const filteredCountries = orderedCountries.filter(isCountryAvailable);

		setCountryStations(filteredCountries);
	};

	useEffectWithoutCleanup(async () => {
		if (navitaireSettings?.abTestSettings && navitaireSettings?.stationSettings && userInfo?.RoleCode) {
			await loadStations();
		}
	}, [navitaireSettings, userInfo?.RoleCode]);

	useEffectWithoutCleanup(() => {
		if (countryStations?.length > 0 && loader) props.stopLoading(loader);
		setCountries(
			countryStations.map(
				(country): Country => ({
					code: country.information.code,
					isAvailableForPeruCompras: true,
					cities: country.regions.reduce(
						(acc, region) => [
							...acc,
							...region.stations.map(
								(s): City => ({
									code: s.information.code,
									countryCode: country.information.code,
									markets: s.markets,
									name: s.information.name,
								})
							),
						],
						[]
					),
					name: country.information.name,
				})
			)
		);
	}, [countryStations]);

	return [countryStations, availableDestinationCountriesForStation];
};
